
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Eye, Shield, Sun } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import glassFacade from "@/assets/glass-facade.jpg";
import glassDoors from "@/assets/glass-doors.jpg";
import glassPartitions from "@/assets/glass-partitions.jpg";
import glassTempered from "@/assets/glass-tempered.jpg";
import glassInsulated from "@/assets/glass-insulated.jpg";
import glassDecorative from "@/assets/glass-decorative.jpg";
import glassMirrors from "@/assets/glass-mirrors.jpg";
import glassTables from "@/assets/glass-tables.jpg";

const Glass = () => {
  const { language, isRTL } = useLanguage();

  const productSlugs = ["facades", "doors", "partitions", "tempered", "insulated", "decorative", "mirrors", "tables"];

  const content = {
    ar: {
      title: "الزجاج",
      subtitle: "نقدم حلول زجاجية متكاملة بأحدث التقنيات والتصاميم العصرية لجميع الاستخدامات",
      productsTitle: "منتجات وخدمات الزجاج",
      viewDetails: "عرض التفاصيل",
      features: [
        { title: "الشفافية والجمال", description: "تصاميم تجمع بين المظهر الأنيق والوظائف العملية لإضفاء لمسة من الفخامة" },
        { title: "الأمان والمتانة", description: "زجاج مقاوم للكسر ومعالج حرارياً للتوافق مع أعلى معايير السلامة" },
        { title: "العزل الحراري", description: "حلول زجاجية عازلة للحرارة والصوت لتوفير الطاقة وتحسين بيئة المعيشة" }
      ],
      products: [
        { title: "واجهات زجاجية", description: "واجهات زجاجية للمباني والأبراج بتصاميم عصرية تضفي مظهراً أنيقاً وفاخراً مع توفير إضاءة طبيعية وإطلالات رائعة." },
        { title: "أبواب سيكوريت", description: "أبواب سيكوريت للمداخل والمحلات التجارية من الزجاج المقوى عالي الأمان بتصاميم أنيقة ومتينة." },
        { title: "قواطع زجاجية", description: "قواطع وفواصل زجاجية للمكاتب والمساحات التجارية توفر الخصوصية مع الحفاظ على الإضاءة الطبيعية." },
        { title: "زجاج معالج حرارياً", description: "زجاج معالج حرارياً ومقاوم للكسر يوفر أعلى مستويات الأمان والسلامة للمباني السكنية والتجارية." },
        { title: "زجاج عازل", description: "زجاج عازل للحرارة والصوت يساهم في توفير الطاقة وتحسين بيئة المعيشة والعمل." },
        { title: "زجاج مزخرف وملون", description: "زجاج مزخرف وملون للديكورات الداخلية يضيف لمسة فنية وجمالية فريدة للمساحات." },
        { title: "مرايا", description: "مرايا بتصاميم وأشكال متنوعة للمنازل والفنادق والمحلات التجارية بجودة عالية وتشطيبات فاخرة." },
        { title: "طاولات وأسطح زجاجية", description: "طاولات وأسطح زجاجية للمنازل والمكاتب من الزجاج المقوى بتصاميم عصرية وأنيقة." }
      ]
    },
    en: {
      title: "Glass",
      subtitle: "We offer comprehensive glass solutions with the latest technologies and modern designs for all uses",
      productsTitle: "Glass Products & Services",
      viewDetails: "View Details",
      features: [
        { title: "Transparency & Beauty", description: "Designs combining elegant appearance with practical functions to add a touch of luxury" },
        { title: "Safety & Durability", description: "Break-resistant and heat-treated glass meeting the highest safety standards" },
        { title: "Thermal Insulation", description: "Heat and sound insulating glass solutions for energy savings and improved living environment" }
      ],
      products: [
        { title: "Glass Facades", description: "Glass facades for buildings and towers with modern designs adding an elegant and luxurious appearance with natural lighting and great views." },
        { title: "Securit Doors", description: "Securit doors for entrances and commercial shops made of high-security tempered glass with elegant and durable designs." },
        { title: "Glass Partitions", description: "Glass partitions and dividers for offices and commercial spaces providing privacy while maintaining natural lighting." },
        { title: "Heat-Treated Glass", description: "Heat-treated and break-resistant glass providing the highest levels of safety and security for residential and commercial buildings." },
        { title: "Insulated Glass", description: "Heat and sound insulating glass contributing to energy savings and improved living and working environment." },
        { title: "Decorative & Colored Glass", description: "Decorative and colored glass for interior decorations adding a unique artistic and aesthetic touch to spaces." },
        { title: "Mirrors", description: "Mirrors in various designs and shapes for homes, hotels, and commercial shops with high quality and luxurious finishes." },
        { title: "Glass Tables & Surfaces", description: "Glass tables and surfaces for homes and offices made of tempered glass with modern and elegant designs." }
      ]
    }
  };

  const t = content[language];
  const images = [glassFacade, glassDoors, glassPartitions, glassTempered, glassInsulated, glassDecorative, glassMirrors, glassTables];

  return (
    <div className="min-h-screen bg-gray-50" dir={isRTL ? "rtl" : "ltr"}>
      <Navbar />
      <div className="container mx-auto px-4 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-center mb-16">
          <h1 className="text-4xl font-kufi font-bold mb-6 text-primary">{t.title}</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto font-kufi">{t.subtitle}</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {t.features.map((feature, index) => (
            <motion.div key={feature.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 * (index + 1) }}>
              <Card>
                <CardHeader className="text-center">
                  {index === 0 && <Eye className="w-12 h-12 mx-auto text-primary mb-4" />}
                  {index === 1 && <Shield className="w-12 h-12 mx-auto text-primary mb-4" />}
                  {index === 2 && <Sun className="w-12 h-12 mx-auto text-primary mb-4" />}
                  <CardTitle className="font-kufi">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent className="text-center font-kufi">
                  <p>{feature.description}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        <h2 className="text-2xl font-kufi font-bold text-center mb-8 text-primary">{t.productsTitle}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {t.products.map((product, index) => (
            <motion.div key={product.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }}>
              <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300 h-full flex flex-col">
                <img src={images[index]} alt={product.title} className="w-full h-48 object-cover" />
                <CardContent className="p-6 flex-grow flex flex-col">
                  <h3 className="text-xl font-kufi font-bold mb-2">{product.title}</h3>
                  <p className="text-gray-600 mb-4 flex-grow">{product.description}</p>
                  <Link to={`/glass/${productSlugs[index]}`}>
                    <Button className="bg-primary hover:bg-primary/90 w-full">{t.viewDetails}</Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Glass;
